/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import Pagination from "../../../../Helper/Pagination";

import {
  showDeleteConfirmation,
  showErrorAlert,
  showSuccessAlert,
} from "../../../../Helper/SweetAlert";

import { MoonLoader } from "react-spinners";
import {
  DeleteService,
  GetService,
  PostService,
  PutService,
} from "../../../../Services/ConstantService";
import { API_URL } from "../../../../Services/ApiService";

export default function AffiliateCommission() {
  const initialValues = {
    min_user: "",
    max_user: null,
    first_commission: "",
    repeat_commission: "",
  };

  const validationSchema = Yup.object({
    min_user: Yup.number()
      .required("Minimum user is required")
      .min(1, "Minimum user should be greater than 0"),

    max_user: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === "" || originalValue === null) {
          return null;
        }

        return value;
      })
      .test(
        "max-validation",
        "Maximum user must be greater than minimum user",
        function (value) {
          // allow blank value
          if (value === null || value === undefined || Number.isNaN(value)) {
            return true;
          }

          return value > Number(this.parent.min_user);
        },
      ),

    first_commission: Yup.number()
      .required("First commission is required")
      .min(0, "Commission cannot be negative")
      .max(100, "Commission cannot exceed 100"),

    repeat_commission: Yup.number()
      .required("Repeat commission is required")
      .min(0, "Commission cannot be negative")
      .max(100, "Commission cannot exceed 100"),
  });

  const [Data, setData] = useState([]);

  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);

  const [total, setTotal] = useState(0);

  const [totalPages, setTotalPages] = useState(0);

  const [dataLimit] = useState(10);

  const [modal, setModal] = useState(false);

  const [editMode, setEditMode] = useState(false);

  const [selectedId, setSelectedId] = useState(null);

  const [editData, setEditData] = useState(initialValues);

  useEffect(() => {
    getList();
  }, [currentPage]);

  const getList = async () => {
    try {
      setLoading(true);

      const res = await GetService(API_URL.GET_AFFILIATE_COMMISSION_LIST, {
        params: {
          limit: dataLimit,
          page: currentPage,
          sorting: "createdAt|asc",
        },
      });

      if (res?.data?.status) {
        setData(res?.data?.data || []);

        setTotal(res?.data?.totalItems || 0);

        setTotalPages(res?.data?.totalPages || 0);
      } else {
        setData([]);
      }
    } catch (error) {
      showErrorAlert("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditMode(false);

    setSelectedId(null);

    setEditData(initialValues);

    setModal(true);
  };

  const openEdit = (item) => {
    setEditMode(true);

    setSelectedId(item.id);

    setEditData({
      min_user: item.min_user,

      max_user: item.max_user ?? null,

      first_commission: item.first_commission,

      repeat_commission: item.repeat_commission,
    });

    setModal(true);
  };

  const submitHandler = async (values, { resetForm, setSubmitting }) => {
    setSubmitting(true);

    let payload = {
      min_user: Number(values.min_user),

      max_user: values.max_user ? Number(values.max_user) : null,

      first_commission: Number(values.first_commission),

      repeat_commission: Number(values.repeat_commission),
    };

    try {
      const res = await (editMode
        ? PutService(`${API_URL.AFFILIATE_COMMISSION}/${selectedId}`, payload)
        : PostService(API_URL.AFFILIATE_COMMISSION, payload));

      if (res?.data?.status) {
        showSuccessAlert(res.data.message, () => {
          setModal(false);
          resetForm();
          getList();
        });
      } else {
        showErrorAlert(res?.data?.message);
      }
    } catch (error) {
      showErrorAlert("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCommission = (id) => {
    showDeleteConfirmation("Commission", async () => {
      const res = await DeleteService(
        `${API_URL.AFFILIATE_COMMISSION}/${id}`,
      );

      if (res?.data?.status) {
        showSuccessAlert(res.data.message, getList);
      } else {
        showErrorAlert(res.data.message);
      }
    });
  };

  return (
    <>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h3>Affiliate Commission</h3>
            </div>

            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/panel/dashboard">Dashboard</Link>
                </li>

                <li className="breadcrumb-item active">Affiliate Commission</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header bg-inverse-light d-flex justify-content-between align-items-center">
              <h3 className="card-title mb-0">Commission List</h3>

              <button className="btn btn-primary btn-sm" onClick={openAdd}>
                <i className="fas fa-plus mr-1"></i>
                Add Commission
              </button>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="LoaderSpinner">
                  <MoonLoader
                    color={"#003bff"}
                    loading={loading}
                    speedMultiplier={0.6}
                    size={50}
                  />

                  <div>Loading... Please wait.</div>
                </div>
              ) : (
                <div className="table-responsive mb-2">
                  <table className="table table-bordered table-striped normal-height">
                    <thead className="">
                      <tr>
                        <th width="80">S.No</th>
                        <th>User Range</th>
                        <th>First Commission %</th>
                        <th>Repeat Commission %</th>
                        <th width="120">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {Data.length > 0 ? (
                        Data.map((item, index) => (
                          <tr key={item.id}>
                            <td>{index + currentPage * dataLimit + 1}</td>

                            <td>
                              {item.max_user !== null &&
                              item.max_user !== undefined
                                ? `${item.min_user} - ${item.max_user}`
                                : `Above ${item.min_user - 1}`}
                            </td>

                            <td>{item.first_commission}%</td>

                            <td>{item.repeat_commission}%</td>

                            <td>
                              <span
                                title="Edit"
                                style={{ cursor: "pointer" }}
                                className="mx-2"
                                onClick={() => openEdit(item)}
                              >
                                <i className="text-info fas fa-edit"></i>{" "}
                              </span>

                              <span
                                title="Delete"
                                style={{ cursor: "pointer" }}
                                className="mx-2"
                                onClick={() => deleteCommission(item.id)}
                              >
                                <i className="text-danger fas fa-trash"></i>{" "}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <Pagination
                counting={currentPage * dataLimit}
                totaldata={total}
                pagecount={totalPages}
                activePage={currentPage}
                onChangePage={(e) => setCurrentPage(e - 1)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* MODAL */}

      {modal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{
            background: "rgba(0,0,0,.5)",
          }}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editMode ? "Edit Commission" : "Add Commission"}
                </h5>

                <button
                  type="button"
                  className="close"
                  onClick={() => setModal(false)}
                >
                  <span>×</span>
                </button>
              </div>

              <Formik
                enableReinitialize
                initialValues={editData}
                validationSchema={validationSchema}
                onSubmit={submitHandler}
              >
                {({ errors, touched, isSubmitting }) => (
                  <Form>
                    <div className="modal-body">
                      <h5 className="mb-2 text-primary">Set User Range</h5>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>
                              Min. User
                              <span className="text-danger">*</span>
                            </label>

                            <Field
                              type="number"
                              name="min_user"
                              className="form-control"
                              min="0"
                              onWheel={(e) => e.target.blur()}
                            />

                            <ErrorMessage
                              name="min_user"
                              component="small"
                              className="text-danger"
                            />
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Max. User</label>

                            <Field
                              type="number"
                              name="max_user"
                              className="form-control"
                              placeholder="Example: 500"
                              min="0"
                              onWheel={(e) => e.target.blur()}
                            />
                            <div className="small text-muted">
                              (Leave blank for unlimited)
                            </div>

                            <ErrorMessage
                              name="max_user"
                              component="small"
                              className="text-danger"
                            />
                          </div>
                        </div>
                      </div>

                      <hr />

                      <h5 className="mb-2 text-primary">Set Commission</h5>

                      <div className="form-group">
                        <label>
                          First Order Commission Percentage
                          <span className="text-danger">*</span>
                        </label>

                        <div className="input-group">
                          <Field
                            type="number"
                            name="first_commission"
                            className="form-control"
                            placeholder="Enter commission percentage"
                            min="0"
                            max="100"
                            onWheel={(e) => e.target.blur()}
                          />

                          <div className="input-group-append">
                            <span className="input-group-text">%</span>
                          </div>
                        </div>

                        <ErrorMessage
                          name="first_commission"
                          component="small"
                          className="text-danger"
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          Repeat Order Commission Percentage
                          <span className="text-danger">*</span>
                        </label>

                        <div className="input-group">
                          <Field
                            type="number"
                            name="repeat_commission"
                            className="form-control"
                            placeholder="Enter commission percentage"
                            min="0"
                            max="100"
                            onWheel={(e) => e.target.blur()}
                          />

                          <div className="input-group-append">
                            <span className="input-group-text">%</span>
                          </div>
                        </div>

                        <ErrorMessage
                          name="repeat_commission"
                          component="small"
                          className="text-danger"
                        />
                      </div>
                    </div>

                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => setModal(false)}
                      >
                        Cancel
                      </button>

                      <button
                        type="submit"
                        className="btn btn-sm btn-primary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            {editMode ? "Updating..." : "Saving..."}
                          </>
                        ) : editMode ? (
                          "Update"
                        ) : (
                          "Save"
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
